import { Button, Chip, ProgressBar, Spinner } from "@heroui/react";
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";
import "./OverlayPrompt.css";

/* ── Public types ────────────────────────────────────────────── */

export type OverlayPlacement = "center" | "bottom-sheet" | "top-banner";
export type OverlaySize = "sm" | "md" | "lg";
export type OverlayTone = "default" | "celebratory" | "warning" | "danger";
export type OverlayActionLayout = "stack" | "row";
export type OverlayIconTone = "default" | "success" | "warning" | "danger" | "primary";

export type OverlayHeroImage = { imageUrl: string; alt?: string };

export type OverlayHero =
  | {
      kind: "image-fan";
      images: OverlayHeroImage[];
      accent?: ReactNode;
      accentBadge?: OverlayAccentBadge;
    }
  | { kind: "single-image"; image: OverlayHeroImage; accentBadge?: OverlayAccentBadge }
  | { kind: "icon"; icon: ReactNode; iconTone?: OverlayIconTone; accentBadge?: OverlayAccentBadge }
  | { kind: "none" };

export type OverlayAccentBadge = {
  label: string;
  tone?: "default" | "success" | "warning" | "danger";
};

export type OverlayAction = {
  id: string;
  label: string;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  startContent?: React.ReactNode;
  onPress?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  autoFocus?: boolean;
};

export type OverlayCountdown = {
  seconds: number;
  onExpire?: () => void;
  label?: string;
};

export type OverlayGate = {
  progress: number;
  label?: string;
};

export type OverlayPromptProps = {
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  placement?: OverlayPlacement;
  size?: OverlaySize;
  tone?: OverlayTone;
  hero?: OverlayHero;
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  footnote?: ReactNode;
  caption?: ReactNode;
  actions?: OverlayAction[];
  actionLayout?: OverlayActionLayout;
  dismissible?: boolean;
  dismissOnBackdrop?: boolean;
  dismissOnEscape?: boolean;
  countdown?: OverlayCountdown;
  gate?: OverlayGate;
  isLoading?: boolean;
  error?: { message: string } | null;
  role?: "dialog" | "alertdialog";
  className?: string;
  testId?: string;
};

/* ── Sub-components ──────────────────────────────────────────── */

const RING_RADIUS = 8;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const CountdownChip = ({
  seconds,
  total,
  label,
}: {
  seconds: number;
  total: number;
  label?: string;
}) => {
  const progress = total > 0 ? seconds / total : 0;
  const displayLabel = label
    ? label.includes("{0}")
      ? label.replace("{0}", `${seconds}s`)
      : `${label} · ${seconds}s`
    : `${seconds}s left`;
  return (
    <div className="op__countdown" aria-live="polite">
      <svg className="op__countdown-ring" viewBox="0 0 22 22" aria-hidden="true">
        <circle
          cx="11"
          cy="11"
          r={RING_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.2"
          strokeWidth="2"
        />
        <circle
          cx="11"
          cy="11"
          r={RING_RADIUS}
          fill="none"
          stroke="hsl(var(--brand-primary, 264 80% 60%))"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
        />
      </svg>
      <span>{displayLabel}</span>
    </div>
  );
};

const ImageFan = ({ images, accent }: { images: OverlayHeroImage[]; accent?: ReactNode }) => {
  const arr = images.slice(0, 3);
  while (arr.length < 3 && arr.length > 0) arr.push(arr[arr.length - 1]!);
  if (arr.length === 0) return null;

  const positions = ["left", "center", "right"] as const;
  return (
    <div className="op__hero-fan" aria-hidden="true">
      {arr.map((img, i) => {
        const pos = positions[i];
        const isCenter = pos === "center";
        return (
          <div key={`${pos}-${img.imageUrl}`} className="op__hero-fan-item" data-pos={pos}>
            <ResponsiveAsset src={img.imageUrl} alt={img.alt ?? ""} draggable="false" />
            {isCenter && accent && <div className="op__hero-fan-accent">{accent}</div>}
          </div>
        );
      })}
    </div>
  );
};

const BADGE_COLOR_MAP: Record<string, "success" | "warning" | "danger" | "accent"> = {
  success: "success",
  warning: "warning",
  danger: "danger",
};

const HeroBlock = ({ hero }: { hero: Exclude<OverlayHero, { kind: "none" }> }) => {
  const accentBadge = "accentBadge" in hero ? hero.accentBadge : undefined;
  const badgeColor = BADGE_COLOR_MAP[accentBadge?.tone ?? ""] ?? "accent";
  return (
    <div className="op__hero">
      {accentBadge && (
        <span className="op__hero-badge">
          <Chip size="sm" color={badgeColor}>
            {accentBadge.label}
          </Chip>
        </span>
      )}
      {hero.kind === "image-fan" && <ImageFan images={hero.images} accent={hero.accent} />}
      {hero.kind === "single-image" && (
        <div className="op__hero-image">
          <ResponsiveAsset src={hero.image.imageUrl} alt={hero.image.alt ?? ""} draggable="false" />
        </div>
      )}
      {hero.kind === "icon" && (
        <div className="op__hero-icon" data-tone={hero.iconTone ?? "default"}>
          {hero.icon}
        </div>
      )}
    </div>
  );
};

const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const ActionButton = ({ action, gateOpen }: { action: OverlayAction; gateOpen: boolean }) => {
  const v = action.variant ?? "primary";
  const isPrimary = v === "primary";
  const disabled = action.isDisabled || action.isLoading || (isPrimary && !gateOpen);
  return (
    <Button
      fullWidth
      size="lg"
      variant={v}
      isDisabled={disabled}
      onPress={action.onPress}
      className="rounded-xl text-base font-semibold"
      data-action-id={action.id}
    >
      {action.isLoading ? (
        <span className="inline-flex items-center gap-2">
          <Spinner aria-hidden="true" color="current" size="sm" />
          {action.label}
        </span>
      ) : action.startContent ? (
        <span className="inline-flex items-center gap-2">
          {action.startContent}
          {action.label}
        </span>
      ) : (
        action.label
      )}
    </Button>
  );
};

/* ── Hooks ────────────────────────────────────────────────────── */

function useOverlayLifecycle(
  open: boolean,
  onOpen?: () => void,
  onClose?: () => void,
  dismissOnEscape?: boolean,
) {
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) onOpen?.();
    wasOpenRef.current = open;
  }, [open, onOpen]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !dismissOnEscape) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismissOnEscape, onClose]);
}

function useOverlayFocus(
  open: boolean,
  actions: OverlayAction[],
  cardRef: React.RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const card = cardRef.current;
      if (!card) return;
      const buttons = card.querySelectorAll<HTMLButtonElement>("button[data-action-id]");
      const autoIdx = actions.findIndex((a) => a.autoFocus);
      if (autoIdx >= 0) {
        buttons[autoIdx]?.focus();
        return;
      }
      const primaryIdx = actions.findIndex((a) => (a.variant ?? "primary") === "primary");
      buttons[primaryIdx >= 0 ? primaryIdx : 0]?.focus();
    }, 220);
    return () => clearTimeout(t);
  }, [open, actions, cardRef]);
}

function useCountdownTimer(open: boolean, countdown?: OverlayCountdown): number {
  const totalSeconds = countdown?.seconds ?? 0;
  const [secondsLeft, setSecondsLeft] = useState(totalSeconds);
  const expiredRef = useRef(false);
  const onExpireRef = useRef(countdown?.onExpire);
  onExpireRef.current = countdown?.onExpire;

  useEffect(() => {
    if (!totalSeconds || !open) return;
    setSecondsLeft(totalSeconds);
    expiredRef.current = false;
  }, [totalSeconds, open]);

  useEffect(() => {
    if (!totalSeconds || !open || secondsLeft <= 0) {
      if (totalSeconds && open && secondsLeft <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        onExpireRef.current?.();
      }
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, totalSeconds, open]);

  return secondsLeft;
}

/* ── Card body sub-components ────────────────────────────────── */

const GateBlock = ({ gate, gateProgress }: { gate: OverlayGate; gateProgress: number }) => (
  <div className="op__gate" aria-live="polite">
    <ProgressBar
      aria-label={gate.label ?? "Progress"}
      value={gateProgress * 100}
      size="sm"
      color="accent"
      className="w-full"
    />
    {gate.label && <div className="op__gate-label">{gate.label}</div>}
  </div>
);

const CardBody = ({
  hero,
  eyebrow,
  titleId,
  title,
  descId,
  description,
  countdown,
  secondsLeft,
  gate,
  gateProgress,
  gateOpen,
  error,
  caption,
  actions,
  actionLayout,
  footnote,
  isLoading,
}: {
  hero?: OverlayHero;
  eyebrow?: string;
  titleId: string;
  title: ReactNode;
  descId: string;
  description?: ReactNode;
  countdown?: OverlayCountdown;
  secondsLeft: number;
  gate?: OverlayGate;
  gateProgress: number;
  gateOpen: boolean;
  error?: { message: string } | null;
  caption?: ReactNode;
  actions: OverlayAction[];
  actionLayout: OverlayActionLayout;
  footnote?: ReactNode;
  isLoading: boolean;
}) => (
  <>
    {hero && hero.kind !== "none" && <HeroBlock hero={hero} />}
    {eyebrow && <div className="op__eyebrow">{eyebrow}</div>}
    <h2 id={titleId} className="op__title" data-step-heading>
      {title}
    </h2>
    {description && (
      <p id={descId} className="op__desc">
        {description}
      </p>
    )}

    {countdown && secondsLeft > 0 && (
      <CountdownChip seconds={secondsLeft} total={countdown.seconds} label={countdown.label} />
    )}

    {gate && !gateOpen && <GateBlock gate={gate} gateProgress={gateProgress} />}

    {error?.message && (
      <div className="op__error" role="alert">
        {error.message}
      </div>
    )}
    {caption && <div className="op__caption">{caption}</div>}

    {actions.length > 0 && (
      <div className="op__actions" data-layout={actionLayout}>
        {actions.map((a) => (
          <ActionButton key={a.id} action={a} gateOpen={gateOpen} />
        ))}
      </div>
    )}

    {footnote && <p className="op__footnote">{footnote}</p>}

    {isLoading && (
      <div className="op__loading" aria-live="polite" aria-busy="true">
        <Spinner size="md" color="accent" />
      </div>
    )}
  </>
);

/* ── Helpers ──────────────────────────────────────────────────── */

const resolveGate = (gate?: OverlayGate) => {
  const progress = gate ? Math.min(1, Math.max(0, gate.progress)) : 1;
  return { gateProgress: progress, gateOpen: progress >= 1 };
};

/* ── Backdrop ─────────────────────────────────────────────────── */

const Backdrop = ({ onClick }: { onClick: () => void }) => (
  <div
    className="op__backdrop"
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") onClick();
    }}
    role="presentation"
    tabIndex={-1}
    aria-hidden="true"
  />
);

/* ── Main component ──────────────────────────────────────────── */

export const OverlayPrompt = (props: OverlayPromptProps) => {
  const {
    open = false,
    onOpen,
    onClose,
    placement = "center",
    size = "md",
    tone = "default",
    actions = [],
    actionLayout = "stack",
    dismissible = false,
    dismissOnBackdrop = true,
    dismissOnEscape = true,
    countdown,
    gate,
    isLoading = false,
    role = "dialog",
    className,
    testId,
  } = props;

  const stableId = useId();
  const titleId = `op-title-${stableId}`;
  const descId = `op-desc-${stableId}`;
  const cardRef = useRef<HTMLDivElement>(null);

  useOverlayLifecycle(open, onOpen, onClose, dismissOnEscape);
  useOverlayFocus(open, actions, cardRef);
  const secondsLeft = useCountdownTimer(open, countdown);

  const { gateProgress, gateOpen } = resolveGate(gate);

  const handleBackdropClick = useCallback(() => {
    if (dismissOnBackdrop) onClose?.();
  }, [dismissOnBackdrop, onClose]);

  return (
    <div
      className={`op ${className ?? ""}`}
      data-open={open}
      data-placement={placement}
      data-size={size}
      data-tone={tone}
      data-testid={testId}
    >
      <Backdrop onClick={handleBackdropClick} />
      <div
        ref={cardRef}
        className="op__card"
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={props.description ? descId : undefined}
      >
        {dismissible && (
          <span className="op__close">
            <Button
              isIconOnly
              size="sm"
              variant="ghost"
              aria-label="Close"
              onPress={() => onClose?.()}
            >
              <CloseIcon />
            </Button>
          </span>
        )}
        <CardBody
          hero={props.hero}
          eyebrow={props.eyebrow}
          titleId={titleId}
          title={props.title}
          descId={descId}
          description={props.description}
          countdown={countdown}
          secondsLeft={secondsLeft}
          gate={gate}
          gateProgress={gateProgress}
          gateOpen={gateOpen}
          error={props.error}
          caption={props.caption}
          actions={actions}
          actionLayout={actionLayout}
          footnote={props.footnote}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default OverlayPrompt;
